import React, { useState, useEffect } from 'react';
import { config } from '../../config';

// Icons
import KeyboardBackspace from '../icons/KeyboardBackspace';
import Timer from '../icons/Timer';
import Countdown from '../icons/Countdown';
import Play from '../icons/Play';
import Pause from '../icons/Pause';
import Stop from '../icons/Stop';

const TimerActivities = ({ cardId, onBack }) => {
    const [sessions, setSessions] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cardInfo, setCardInfo] = useState(null);

    useEffect(() => {
        const fetchSessions = async () => {
            try {
                setLoading(true);

                // Fetch card info
                const cardResponse = await fetch(`${config.API_URI}/v1/cards/${cardId}`, {
                    credentials: 'include'
                });
                if (cardResponse.ok) {
                    const cardData = await cardResponse.json();
                    setCardInfo(cardData);
                }

                // Fetch timer sessions
                const sessionsResponse = await fetch(`${config.API_URI}/v1/timers/cards/${cardId}/timer/sessions?limit=50`, {
                    credentials: 'include'
                });

                if (sessionsResponse.ok) {
                    const data = await sessionsResponse.json();
                    setSessions(data.sessions || []);
                    setCurrentSession(data.currentSession);
                    setStats(data.stats);
                } else {
                    console.error('Failed to fetch timer sessions');
                    setSessions([]);
                    setCurrentSession(null);
                    setStats(null);
                }
            } catch (error) {
                console.error('Error fetching timer sessions:', error);
                setSessions([]);
                setCurrentSession(null);
                setStats(null);
            } finally {
                setLoading(false);
            }
        };

        fetchSessions();
    }, [cardId]);

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else if (date.toDateString() === yesterday.toDateString()) {
            return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else {
            return date.toLocaleDateString([], { 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    const getTotalTimeToday = () => {
        const today = new Date().toDateString();
        return sessions
            .filter(session => new Date(session.startTime).toDateString() === today)
            .reduce((total, session) => total + (session.duration || 0), 0);
    };

    const getTotalTimeThisWeek = () => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        return sessions
            .filter(session => new Date(session.startTime) >= oneWeekAgo)
            .reduce((total, session) => total + (session.duration || 0), 0);
    };

    const getSessionStatusIcon = (session) => {
        if (session.status === 'active') {
            return <Play size={12} color="var(--success-color)" />;
        } else if (session.status === 'paused') {
            return <Pause size={12} color="var(--warning-color)" />;
        } else if (session.status === 'completed') {
            return <Stop size={12} color="var(--success-color)" />;
        } else {
            return <Stop size={12} color="var(--error-color)" />;
        }
    };

    const getSessionStatusText = (session) => {
        switch (session.status) {
            case 'active': return 'Running';
            case 'paused': return 'Paused';
            case 'completed': return 'Completed';
            case 'stopped': return 'Stopped';
            default: return session.status;
        }
    };

    if (loading) {
        return (
            <div className="timer-activities">
                <div className="timer-activities-header">
                    <button className="back-button" onClick={onBack}>
                        <KeyboardBackspace size={18} />
                    </button>
                    <h3>Loading...</h3>
                </div>
                <div className="timer-activities-loading">
                    <div className="loading-spinner"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="timer-activities">
            <div className="timer-activities-header">
                <button className="back-button" onClick={onBack}>
                    <KeyboardBackspace size={18} />
                </button>
                <div className="timer-activities-title">
                    <h3>Timer Activities</h3>
                    {cardInfo && (
                        <p className="card-title">{cardInfo.title}</p>
                    )}
                </div>
            </div>

            <div className="timer-activities-content">
                {/* Summary Stats */}
                <div className="timer-stats">
                    <div className="stat-item">
                        <span className="stat-label">Today</span>
                        <span className="stat-value">{formatTime(getTotalTimeToday())}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">This Week</span>
                        <span className="stat-value">{formatTime(getTotalTimeThisWeek())}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Total Sessions</span>
                        <span className="stat-value">{sessions.length}</span>
                    </div>
                    {stats && (
                        <div className="stat-item">
                            <span className="stat-label">Avg Duration</span>
                            <span className="stat-value">{formatTime(Math.round(stats.avgDuration || 0))}</span>
                        </div>
                    )}
                </div>

                {/* Sessions */}
                <div className="sessions-container">
                    {sessions.length === 0 && !currentSession ? (
                        <div className="empty-state">
                            <Timer size={24} color="var(--muted-text-color)" />
                            <h3>No sessions yet</h3>
                            <p>Start a timer to track your work sessions</p>
                        </div>
                    ) : (
                        <div className="sessions-list">
                            {/* Current Session */}
                            {currentSession && (
                                <div className="session-card active">
                                    <div className="session-header">
                                        <div className="session-info">
                                            <div className="session-type">
                                                {currentSession.mode === 'countdown' ? (
                                                    <Countdown size={14} color="var(--success-color)" />
                                                ) : (
                                                    <Timer size={14} color="var(--success-color)" />
                                                )}
                                                <span>{currentSession.mode === 'countdown' ? 'Countdown' : 'Stopwatch'}</span>
                                            </div>
                                            <div className="session-status running">
                                                {getSessionStatusIcon(currentSession)}
                                                <span>{getSessionStatusText(currentSession)}</span>
                                            </div>
                                        </div>
                                        <div className="session-duration">
                                            {formatTime(currentSession.duration)}
                                        </div>
                                    </div>

                                    <div className="session-details">
                                        <div className="session-time">
                                            Started {formatDate(currentSession.startTime)}
                                        </div>

                                        {(currentSession.pauseCount > 0 || currentSession.totalPausedTime > 0) && (
                                            <div className="session-stats">
                                                {currentSession.pauseCount > 0 && (
                                                    <span>{currentSession.pauseCount} pause{currentSession.pauseCount > 1 ? 's' : ''}</span>
                                                )}
                                                {currentSession.totalPausedTime > 0 && (
                                                    <span>{formatTime(currentSession.totalPausedTime)} paused</span>
                                                )}
                                            </div>
                                        )}

                                        {currentSession.mode === 'countdown' && currentSession.targetDuration && (
                                            <div className="session-target">
                                                Target: {formatTime(currentSession.targetDuration)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Session History */}
                            {sessions.length > 0 && (
                                <>
                                    {currentSession && <div className="sessions-divider">Recent Sessions</div>}

                                    {sessions.map((session, index) => (
                                        <div key={session.id || index} className="session-card">
                                            <div className="session-header">
                                                <div className="session-info">
                                                    <div className="session-type">
                                                        {session.mode === 'countdown' ? (
                                                            <Countdown size={14} color="var(--muted-text-color)" />
                                                        ) : (
                                                            <Timer size={14} color="var(--muted-text-color)" />
                                                        )}
                                                        <span>{session.mode === 'countdown' ? 'Countdown' : 'Stopwatch'}</span>
                                                    </div>
                                                </div>
                                                <div className="session-duration">
                                                    {formatTime(session.duration)}
                                                </div>
                                            </div>

                                            <div className="session-details">
                                                <div className="session-time">
                                                    {formatDate(session.startTime)}
                                                    {session.endTime && (
                                                        <span className="session-end"> → {formatDate(session.endTime)}</span>
                                                    )}
                                                </div>

                                                {(session.pauseCount > 0 || session.totalPausedTime > 0) && (
                                                    <div className="session-stats">
                                                        {session.pauseCount > 0 && (
                                                            <span>{session.pauseCount} pause{session.pauseCount > 1 ? 's' : ''}</span>
                                                        )}
                                                        {session.totalPausedTime > 0 && (
                                                            <span>{formatTime(session.totalPausedTime)} paused</span>
                                                        )}
                                                    </div>
                                                )}

                                                {session.mode === 'countdown' && session.targetDuration && (
                                                    <div className="session-target">
                                                        Target: {formatTime(session.targetDuration)}
                                                    </div>
                                                )}

                                                {session.notes && (
                                                    <div className="session-notes">
                                                        {session.notes}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TimerActivities;
